/**
 * Database Configuration
 * ======================
 * Controls whether to use local or production Supabase database.
 *
 * Configuration is controlled via environment variable:
 * - Set VITE_USE_LOCAL_DATABASE=true in your .env for local development
 * - Set VITE_USE_LOCAL_DATABASE=false or leave unset for production
 *
 * This ensures each developer can control their environment without affecting others.
 *
 * Port Configuration (Exercise Toolbox uses 54600 series):
 * - API/PostgREST: 54600
 * - Database: 54601
 * - Studio: 54602
 * - Inbucket: 54603
 */

export interface SupabaseConfig {
  url: string
  anonKey: string
  isLocal: boolean
}

/**
 * Check if a URL looks like a production Supabase URL
 */
function isProductionUrl(url: string): boolean {
  return url.includes('supabase.co') || url.includes('supabase.in')
}

export const DATABASE_CONFIG = {
  /**
   * Toggle between local and production Supabase database
   * Controlled by VITE_USE_LOCAL_DATABASE environment variable
   * Default: false (use production database for safety)
   */
  USE_LOCAL_DATABASE:
    import.meta.env.VITE_USE_LOCAL_DATABASE === 'true',

  /**
   * Local Supabase URL (from `npx supabase start` output)
   * Exercise Toolbox uses port 54600
   */
  LOCAL_URL:
    import.meta.env.VITE_LOCAL_SUPABASE_URL || 'http://127.0.0.1:54600',

  /**
   * Local Supabase anon key (from `npx supabase start` output)
   */
  LOCAL_ANON_KEY: import.meta.env.VITE_LOCAL_SUPABASE_ANON_KEY || '',

  /**
   * Production Supabase URL
   */
  PRODUCTION_URL: import.meta.env.VITE_SUPABASE_URL || '',

  /**
   * Production Supabase anon key
   */
  PRODUCTION_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY || '',

  /**
   * Get the current Supabase configuration based on environment settings
   * Returns the appropriate URL and anon key for local or production
   *
   * Safety feature: If local mode is enabled but URL looks like production,
   * it will warn and refuse to create the client
   */
  getSupabaseConfig(): SupabaseConfig {
    if (this.USE_LOCAL_DATABASE) {
      // Safety check: Make sure we're not accidentally using production URL in local mode
      if (isProductionUrl(this.LOCAL_URL)) {
        console.error(
          '🚨 SAFETY ERROR: VITE_USE_LOCAL_DATABASE=true but VITE_LOCAL_SUPABASE_URL contains a production domain!'
        )
        console.error(
          '   Local URL should be http://127.0.0.1:54600 or similar, not a supabase.co domain.'
        )
        throw new Error(
          'Invalid configuration: Local mode enabled but production URL detected. Check your .env file.'
        )
      }

      console.log('🔧 Using LOCAL Supabase Database')
      console.log('=== Database Configuration ===')
      console.log(`URL: ${this.LOCAL_URL}`)
      console.log('Mode: LOCAL')
      console.log('==============================')

      return {
        url: this.LOCAL_URL,
        anonKey: this.LOCAL_ANON_KEY,
        isLocal: true,
      }
    }

    console.log('🚀 Using PRODUCTION Supabase Database')
    console.log('=== Database Configuration ===')
    console.log(`URL: ${this.PRODUCTION_URL}`)
    console.log('Mode: PRODUCTION')
    console.log('==============================')

    return {
      url: this.PRODUCTION_URL,
      anonKey: this.PRODUCTION_ANON_KEY,
      isLocal: false,
    }
  },

  /**
   * Check if we're using local database
   */
  isUsingLocalDatabase(): boolean {
    return this.USE_LOCAL_DATABASE
  },

  /**
   * Validate that required configuration is present
   * Returns true if valid, throws error if invalid
   */
  validate(): boolean {
    const config = this.USE_LOCAL_DATABASE
      ? { url: this.LOCAL_URL, key: this.LOCAL_ANON_KEY, mode: 'local' }
      : {
          url: this.PRODUCTION_URL,
          key: this.PRODUCTION_ANON_KEY,
          mode: 'production',
        }

    if (!config.url) {
      throw new Error(
        `Missing Supabase URL for ${config.mode} mode. Check your .env file.`
      )
    }

    if (!config.key) {
      console.warn(
        `⚠️ Missing Supabase anon key for ${config.mode} mode. Some features may not work.`
      )
    }

    return true
  },

  /**
   * Log current configuration status (detailed)
   */
  logStatus() {
    console.log('📊 Database Configuration Status:')
    console.log(`   Mode: ${this.USE_LOCAL_DATABASE ? 'LOCAL' : 'PRODUCTION'}`)
    console.log(`   URL: ${this.USE_LOCAL_DATABASE ? this.LOCAL_URL : this.PRODUCTION_URL}`)
    console.log(`   Anon Key: ${(this.USE_LOCAL_DATABASE ? this.LOCAL_ANON_KEY : this.PRODUCTION_ANON_KEY) ? '✓ Set' : '✗ Missing'}`)
  },
}

/**
 * Helper to toggle database mode at runtime (primarily for testing)
 * Note: This does NOT recreate the Supabase client - for that you'd need to reload the page
 */
export function setDatabaseMode(useLocal: boolean) {
  DATABASE_CONFIG.USE_LOCAL_DATABASE = useLocal
  console.log(
    `Database mode changed to: ${useLocal ? 'LOCAL' : 'PRODUCTION'}`
  )
}

// For backwards compatibility with existing code
export const databaseConfig = DATABASE_CONFIG
